/**
 * Uma linha da aba Documentos (E205.F4.T02/T03, ADR-0147 §2.5): nome, papel, páginas e leitura,
 * código declarado × custódia e as ações — **Abrir** (PDF e imagem, no painel ao lado), **Confirmar
 * leitura** (o cartão do apoio ou da imagem do documento de identidade, aberto embaixo da linha),
 * **Rever leitura** (o painel do dado do campo que pede revisão) e **Baixar**.
 *
 * O que não abre diz por quê, na própria linha: o tipo que o painel não mostra, o excluído; e o
 * download que o servidor recusa diz a causa (custódia, recorte, arquivo fora do caso).
 *
 * A linha tem a âncora do documento (`#documento-<id>`): a pendência informativa leva até ela e abre
 * a confirmação.
 *
 * E207.F6.T01 (owner, 01/11): a lista é a tabela do design system. Cada documento é um grupo de
 * linhas (`TableBody`): a linha do documento e, aberta, a linha da confirmação, que ocupa as cinco
 * colunas. Assim as colunas alinham em todas as linhas e as ações ficam numa coluna só, à direita.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { DocumentoDeApoioCartao } from '@/components/ExameEletronico/DocumentoDeApoioCartao';
import { HashComOProtocoloBadge } from '@/components/ExameEletronico/HashComOProtocolo';
import { LeituraPropostaDaImagem } from '@/components/ExameEletronico/LeituraPropostaDaImagem';
import { TableBody, TableCell, TableRow } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { ancoraDoDocumento } from '@/lib/mesaEmAbas';
import { cn } from '@/lib/utils';

import type { LinhaDoDocumento } from './linhasDosDocumentos';
import { AcoesDaLinha } from './AcoesDaLinha';

const t = ptBR.documentosDoCaso;

/** As colunas da tabela: Letra, Documento, Papel, Páginas e leitura, Código declarado × custódia,
 * Utilizado no exame, Ações. */
export const COLUNAS_DA_TABELA = 7;

/** Owner, 04/11: "Sim" com a letra da seção 2, "Não" sem ela; sem seção 2 composta, "—". */
export function usadoNoExame(letra: string | null, exameComposto: boolean): string {
  if (!exameComposto) return t.usado.semExame;
  return letra !== null ? t.usado.sim : t.usado.nao;
}

/** E216.F3 (GUS-317 item 5): a letra da seção 2 do exame; sem ela, "—". */
function LetraNaTabela({ letra }: { letra: string | null }): JSX.Element {
  return (
    <TableCell className="text-body-sm font-semibold tabular-nums" data-testid="linha-letra">
      {letra ?? t.semLetra}
    </TableCell>
  );
}

function UsadoNaTabela({
  letra,
  exameComposto,
}: {
  letra: string | null;
  exameComposto: boolean;
}): JSX.Element {
  const usado = exameComposto && letra !== null;
  return (
    <TableCell
      className={cn('text-body-sm', usado ? 'font-medium text-fg' : 'text-fg-muted')}
      data-testid="linha-usado"
    >
      {usadoNoExame(letra, exameComposto)}
    </TableCell>
  );
}

/** O nome do arquivo e a pasta dele dentro do pacote (`null`: arquivo solto). */
export function partesDoNome(nome: string): { arquivo: string; pasta: string | null } {
  const corte = Math.max(nome.lastIndexOf('/'), nome.lastIndexOf('\\'));
  if (corte <= 0 || corte === nome.length - 1) return { arquivo: nome, pasta: null };
  return { arquivo: nome.slice(corte + 1), pasta: nome.slice(0, corte) };
}

/**
 * O nome do documento na primeira coluna, com o nome inteiro na dica. E209.F2.T07 (GUS-296 item 13b):
 * o arquivo de dentro do pacote mostra o próprio nome em destaque e a pasta embaixo — antes o corte no
 * fim deixava `content.json` e `timeStamp.tst` com o mesmo começo (`Manifesto.zip/Evidências/…`).
 */
export function NomeNaTabela({ nome }: { nome: string }): JSX.Element {
  const { arquivo, pasta } = partesDoNome(nome);
  return (
    <div className="max-w-[36ch]" title={nome}>
      <p className="truncate text-body-sm font-medium text-fg" data-testid="nome-na-tabela-arquivo">
        {arquivo}
      </p>
      {pasta !== null ? (
        <p className="truncate text-caption text-fg-muted" data-testid="nome-na-tabela-pasta">
          {pasta}
        </p>
      ) : null}
    </div>
  );
}

function paginasELeitura(linha: LinhaDoDocumento): string {
  const partes: string[] = [];
  if (linha.paginas !== null) {
    partes.push(
      linha.paginas === 1 ? t.umaPagina : t.paginas.replace('{n}', String(linha.paginas)),
    );
  }
  if (linha.leitura !== null) partes.push(t.leitura[linha.leitura]);
  return partes.length > 0 ? partes.join(' · ') : t.semPaginas;
}

function Confirmacao({
  caseId,
  linha,
  concluido,
  onConfirmado,
}: {
  caseId: string;
  linha: LinhaDoDocumento;
  concluido: boolean;
  onConfirmado: () => void;
}): JSX.Element | null {
  if (linha.cartoes.length === 0) return null;
  return (
    <div className="space-y-sc-3">
      {linha.cartoes.map((cartao) => (
        <div key={cartao.tipo} data-testid="linha-confirmacao" data-tipo={cartao.tipo}>
          {cartao.tipo === 'apoio' ? (
            <ul>
              <DocumentoDeApoioCartao
                caseId={caseId}
                documento={cartao.documento}
                concluido={concluido}
                onConfirmado={onConfirmado}
              />
            </ul>
          ) : (
            <LeituraPropostaDaImagem
              caseId={caseId}
              leitura={cartao.leitura}
              bloqueado={concluido}
              onConfirmada={onConfirmado}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export interface LinhaDeDocumentoProps {
  caseId: string;
  linha: LinhaDoDocumento;
  concluido: boolean;
  /** Owner, 04/11: a seção 2 do exame foi composta (a coluna "Utilizado no exame" afirma). */
  exameComposto?: boolean;
  onConfirmado: () => void;
  /** E214.F1 (GUS-314): o recorte manual desta linha. */
  onRecortar?: ((linha: LinhaDoDocumento) => void) | undefined;
  /** E216.F4 (GUS-317 item 3): a exclusão desta linha. */
  onExcluir?: ((linha: LinhaDoDocumento) => void) | undefined;
}

export function LinhaDeDocumento({
  caseId,
  linha,
  concluido,
  exameComposto = false,
  onConfirmado,
  onRecortar,
  onExcluir,
}: LinhaDeDocumentoProps): JSX.Element {
  const ancora = ancoraDoDocumento(linha.id);
  const { hash, key } = useLocation();
  const apontada = decodeURIComponent(hash.slice(1)) === ancora;
  const [aberta, setAberta] = useState(apontada);
  // A pendência que aponta a linha abre a confirmação (o mesmo link de novo pede de novo).
  useEffect(() => {
    if (apontada) setAberta(true);
  }, [apontada, key]);
  const excluido = linha.abre === 'excluido';
  return (
    <TableBody
      id={ancora}
      className={cn('scroll-mt-sc-6', excluido && 'text-fg-muted')}
      data-testid="linha-do-documento"
      data-documento-id={linha.id}
      data-abre={linha.abre}
      data-leitura={linha.leitura ?? ''}
    >
      <TableRow>
        <LetraNaTabela letra={linha.letra} />
        <TableCell>
          <NomeNaTabela nome={linha.nome} />
          {linha.abre !== 'painel' ? (
            <p className="mt-sc-1 text-caption text-fg-muted" data-testid="linha-nao-abre">
              {excluido ? t.naoAbre.excluido : t.naoAbre.tipo}
            </p>
          ) : null}
        </TableCell>
        <TableCell className="text-body-sm text-fg-muted">{linha.papel}</TableCell>
        <TableCell
          className={cn(
            'text-body-sm',
            linha.leitura === 'a_confirmar' ? 'text-warning-700' : 'text-fg-muted',
          )}
          data-testid="linha-leitura"
        >
          {paginasELeitura(linha)}
        </TableCell>
        <TableCell>
          {linha.custodia !== null ? (
            <HashComOProtocoloBadge
              estado={linha.custodia}
              titulo={linha.nome}
              testId="linha-custodia"
            />
          ) : (
            <span className="text-body-sm text-fg-muted">{t.semCustodia}</span>
          )}
        </TableCell>
        <UsadoNaTabela letra={linha.letra} exameComposto={exameComposto} />
        <TableCell className="whitespace-nowrap text-right">
          <AcoesDaLinha
            caseId={caseId}
            linha={linha}
            aberta={aberta}
            onConfirmar={() => setAberta((a) => !a)}
            onRecortar={onRecortar === undefined ? undefined : () => onRecortar(linha)}
            onExcluir={onExcluir === undefined ? undefined : () => onExcluir(linha)}
          />
        </TableCell>
      </TableRow>
      {aberta ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={COLUNAS_DA_TABELA} className="bg-surface-2 p-sc-3">
            <Confirmacao
              caseId={caseId}
              linha={linha}
              concluido={concluido}
              onConfirmado={onConfirmado}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </TableBody>
  );
}
